const STORAGE_PREFIX = 'website-design-workspace-draft'
const STORAGE_VERSION = 2

function getDraftStorageKey({ organizationId, designId }) {
  return `${STORAGE_PREFIX}:${organizationId || 'unknown'}:${designId || 'default'}`
}

function serializeWorkspaceDraftValue(value) {
  try {
    return JSON.stringify(value)
  } catch {
    return null
  }
}

function readStoredWorkspaceDraftValues({ organizationId, designId, enabled = false }) {
  if (!enabled || typeof window === 'undefined') {
    return undefined
  }

  try {
    const storedValue = window.sessionStorage.getItem(
      getDraftStorageKey({ organizationId, designId })
    )

    if (!storedValue) {
      return undefined
    }

    const parsedValue = JSON.parse(storedValue)

    if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
      return undefined
    }

    if (parsedValue.version !== STORAGE_VERSION) {
      clearWorkspaceDraftValues({ organizationId, designId })
      return undefined
    }

    const values = parsedValue.values

    if (!values || typeof values !== 'object' || Array.isArray(values)) {
      clearWorkspaceDraftValues({ organizationId, designId })
      return undefined
    }

    return {
      baselineSignature:
        typeof parsedValue.baselineSignature === 'string' ? parsedValue.baselineSignature : null,
      values,
    }
  } catch {
    return undefined
  }
}

export function readWorkspaceDraftSnapshot({
  organizationId,
  designId,
  fallbackValues,
  enabled = false,
}) {
  const storedDraft = readStoredWorkspaceDraftValues({ organizationId, designId, enabled })
  const storedValues = storedDraft?.values
  const storedBaselineSignature = storedDraft?.baselineSignature
  const storedSignature = serializeWorkspaceDraftValue(storedValues)
  const fallbackSignature = serializeWorkspaceDraftValue(fallbackValues)

  if (
    storedValues !== undefined &&
    typeof storedBaselineSignature === 'string' &&
    fallbackSignature !== null &&
    storedBaselineSignature !== fallbackSignature
  ) {
    clearWorkspaceDraftValues({ organizationId, designId })

    return {
      hasDraft: false,
      values: fallbackValues,
    }
  }

  // A stale stored draft that already matches the saved design should not keep
  // the workspace in a permanently dirty-looking state.
  if (
    storedValues !== undefined &&
    storedSignature !== null &&
    fallbackSignature !== null &&
    storedSignature === fallbackSignature
  ) {
    clearWorkspaceDraftValues({ organizationId, designId })

    return {
      hasDraft: false,
      values: fallbackValues,
    }
  }

  return {
    hasDraft: storedValues !== undefined,
    values: storedValues ?? fallbackValues,
  }
}

export function writeWorkspaceDraftValues({ organizationId, designId, baselineValues, values }) {
  if (typeof window === 'undefined') {
    return
  }

  const baselineSignature = serializeWorkspaceDraftValue(baselineValues)

  if (typeof baselineSignature !== 'string') {
    return
  }

  try {
    window.sessionStorage.setItem(
      getDraftStorageKey({ organizationId, designId }),
      JSON.stringify({
        version: STORAGE_VERSION,
        baselineSignature,
        values,
      })
    )
  } catch {
    // Ignore session storage failures in local/private browsing contexts.
  }
}

export function clearWorkspaceDraftValues({ organizationId, designId }) {
  if (typeof window === 'undefined') {
    return
  }

  window.sessionStorage.removeItem(getDraftStorageKey({ organizationId, designId }))
}
